<!-- Assessment Attempter -->
<app-navbar *ngIf="!isFullscreen"></app-navbar>

<section class="assessment-shell" [class.fullscreen-mode]="isFullscreen" (scroll)="onAssessmentScroll()">

  <!-- LOADING OVERLAY -->
  <div class="loading-overlay" [class.visible]="isLoading">
    <div class="loader-content">
      <div class="spinner-ring">
        <div></div><div></div><div></div><div></div>
      </div>
      <p class="loader-text">{{ loadingText }}</p>
    </div>
  </div>

  <video #proctoringVideo class="proctoring-video" autoplay muted playsinline aria-hidden="true"></video>

  <div
    class="assessment-container"
    [ngSwitch]="isUnsupportedScreen ? 'unsupported-screen' : (cameraRequiredForResume ? 'camera-required' : getStateOfAssessment())">

    <!-- ─── UNSUPPORTED DEVICE / VIEWPORT ─── -->
    <div *ngSwitchCase="'unsupported-screen'" class="completion-state fade-in device-block-state">
      <div class="completion-card device-block-card">
        <div class="completion-icon device-block-icon" aria-hidden="true">
          <mat-icon>desktop_windows</mat-icon>
        </div>
        <h2>Laptop or desktop required</h2>
        <p>
          This assessment cannot be started on a phone or tablet. Its timing and tab-security
          checks require a laptop or desktop browser window.
        </p>
        <p>Your assessment will not begin on this screen size.</p>
        <button class="btn-primary-action" (click)="goToAssessments()">
          <span>Back to Assessments</span>
        </button>
      </div>
    </div>

    <!-- ─── CAMERA REQUIRED TO RESUME ─── -->
    <div *ngSwitchCase="'camera-required'" class="completion-state fade-in">
      <div class="completion-card camera-required-card">
        <div class="completion-icon camera-required-icon" aria-hidden="true">
          <mat-icon>videocam</mat-icon>
        </div>
        <h2>Camera access required</h2>
        <p>
          Camera access must remain active during the assessment to maintain assessment integrity.
        </p>
        <p *ngIf="isTimerRecoveryLoading">Restoring your assessment timer…</p>
        <p *ngIf="timerRecoveryError" class="camera-error">{{ timerRecoveryError }}</p>
        <p *ngIf="cameraErrorMessage" class="camera-error">{{ cameraErrorMessage }}</p>
        <button
          class="btn-primary-action"
          (click)="enableCameraAndResume()"
          [disabled]="isRequestingCamera || isTimerRecoveryLoading || !!timerRecoveryError">
          <span>{{ isTimerRecoveryLoading ? 'Restoring timer…' : (isRequestingCamera ? 'Requesting access…' : 'Allow camera and continue') }}</span>
        </button>
      </div>
    </div>

    <!-- ─── RULES STATE ─── -->
    <div *ngSwitchCase="'rule'" class="state-panel fade-in">
      <app-assessment-rules></app-assessment-rules>
      <div class="camera-consent-notice" role="note">
        <mat-icon aria-hidden="true">photo_camera</mat-icon>
        <div>
          <strong>Camera monitoring is required</strong>
          <p>By beginning, you allow Konvac to keep your camera active to ensure assessment integrity.</p>
          <p *ngIf="cameraErrorMessage" class="camera-error">{{ cameraErrorMessage }}</p>
        </div>
      </div>
      <div class="action-bar mt-4">
        <button class="btn-primary-action" (click)="onNext()" [disabled]="isRequestingCamera || isLoading">
          <span>{{ isRequestingCamera ? 'Requesting camera…' : 'Allow camera & begin' }}</span>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="5" y1="12" x2="19" y2="12"></line>
            <polyline points="12 5 19 12 12 19"></polyline>
          </svg>
        </button>
      </div>
    </div>

    <!-- ─── QUESTION STATE ─── -->
    <div *ngSwitchCase="'question'" class="question-layout" [class.timer-docked]="isTimerDocked">

      <div *ngIf="isCameraActive" class="camera-active-indicator" role="status">
        <span class="camera-active-dot" aria-hidden="true"></span>
        Camera monitoring active
      </div>

      <!-- TOP BAR -->
      <div class="top-bar" #topBar>
        <!-- Overall Timer -->
        <div class="overall-timer">
          <svg class="timer-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
          <span class="timer-label">Total</span>
          <span class="timer-value" [class.warning]="currentMinutes < 2">
            {{ currentMinutes | number:'2.0-0' }}:{{ currentSeconds | number:'2.0-0' }}
          </span>
        </div>

        <!-- Question Progress -->
        <div class="question-progress">
          <span class="progress-label">Question in Progress</span>
        </div>

        <!-- Per-Question Timer Ring -->
        <div class="question-timer-wrap">
          <svg class="ring-svg" viewBox="0 0 56 56">
            <circle class="ring-bg" cx="28" cy="28" r="24" />
            <circle
              class="ring-fg"
              cx="28" cy="28" r="24"
              [style.stroke-dashoffset]="questionTimerDashOffset"
              [class.ring-warning]="questionSeconds < 30 && questionMinutes === 0"
            />
          </svg>
          <div class="ring-text">
            <span [class.warning-text]="questionSeconds < 30 && questionMinutes === 0">
              {{ questionMinutes }}:{{ questionSeconds | number:'2.0-0' }}
            </span>
          </div>
        </div>
      </div>

      <!-- The same live timers, repositioned when the top bar leaves the viewport. -->
      <aside
        class="docked-timer"
        [class.visible]="isTimerDocked"
        aria-hidden="true"
      >
        <div class="docked-timer-section">
          <svg class="timer-icon" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
          <span class="docked-timer-label">Total</span>
          <span class="docked-total-value" [class.warning]="currentMinutes < 2">
            {{ currentMinutes | number:'2.0-0' }}:{{ currentSeconds | number:'2.0-0' }}
          </span>
        </div>

        <div class="docked-divider"></div>

        <div class="docked-timer-section">
          <span class="docked-timer-label">Question</span>
          <div class="question-timer-wrap docked-question-timer">
            <svg class="ring-svg" viewBox="0 0 56 56">
              <circle class="ring-bg" cx="28" cy="28" r="24" />
              <circle
                class="ring-fg"
                cx="28"
                cy="28"
                r="24"
                [style.stroke-dashoffset]="questionTimerDashOffset"
                [class.ring-warning]="questionSeconds < 30 && questionMinutes === 0"
              />
            </svg>
            <div class="ring-text">
              <span [class.warning-text]="questionSeconds < 30 && questionMinutes === 0">
                {{ questionMinutes }}:{{ questionSeconds | number:'2.0-0' }}
              </span>
            </div>
          </div>
        </div>
      </aside>

      <!-- QUESTION CARD with slide animation -->
      <div class="question-area" [class.slide-in]="questionAnimating" [class.slide-out]="questionExiting">
        <app-assessment-question></app-assessment-question>
      </div>

      <!-- SUBMIT BAR -->
      <div class="submit-bar">
        <button class="btn-submit" (click)="onAnswer()" [disabled]="isLoading">
          <span>Submit Answer</span>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </button>
      </div>

    </div>

    <!-- ─── COMPLETED STATE ─── -->
    <div *ngSwitchCase="'completed'" class="completion-state fade-in">
      <div class="completion-card">
        <div class="completion-icon" aria-hidden="true">
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="7 12.5 10.5 16 17 8"></polyline>
          </svg>
        </div>
        <h2>Congratulations ! you have competed the assessment successfully !</h2>
        <p *ngIf="!completionError">You completed {{ completedAnsweredCount }} questions.</p>
        <p *ngIf="completionError" class="completion-error">{{ completionError }}</p>
        <button class="btn-primary-action" (click)="goToAssessments()">
          <span>Back to Assessments</span>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
        </button>
      </div>
    </div>

    <!-- ─── FAILED / INTEGRITY STATE ─── -->
    <div *ngSwitchCase="'failed'" class="completion-state fade-in">
      <div class="completion-card integrity-failure-card">
        <div class="completion-icon integrity-failure-icon" aria-hidden="true">
          <mat-icon>gpp_bad</mat-icon>
        </div>
        <h2>Assessment attempt ended</h2>
        <p>{{ integrityFailureMessage || 'This attempt has been marked as failed.' }}</p>
        <p class="integrity-support">Switching tabs, leaving the assessment screen, or stopping camera access is not allowed during an active attempt. Contact support if this happened because of a technical problem.</p>
        <button class="btn-primary-action" (click)="goToAssessments()">
          <span>Back to Assessments</span>
        </button>
      </div>
    </div>

    <!-- ─── DEFAULT / ERROR STATE ─── -->
    <div *ngSwitchDefault class="state-panel fade-in error-state">
      <div class="error-card">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <h3>Something went wrong</h3>
        <p>Your session may have expired. Please start again.</p>
        <button class="btn-primary-action" (click)="onRestartAssessment()" [disabled]="isLoading">Restart</button>
      </div>
    </div>

  </div>
</section>

<app-footer *ngIf="!isFullscreen"></app-footer>
